import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Cookies from 'js-cookie';
import { Stomp } from '@stomp/stompjs';

import Grid from '@material-ui/core/Grid';
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Chip,
    Tooltip,
    Badge,
    CircularProgress
} from '@mui/material';
import Skeleton from '@material-ui/lab/Skeleton';
import { ThemeProvider as CoreThemeProvider, createTheme as createCoreTheme } from '@material-ui/core/styles';
import { ThemeProvider as MuiThemeProvider, createTheme as createMuiTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SecurityIcon from '@mui/icons-material/Security';
import SensorsIcon from '@mui/icons-material/Sensors';
import SignalCellularConnectedNoInternet4BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet4Bar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import NavegadorGestao from "../../../../../components/NavBarGestao";
import api from '../../../../../services/api';

import icone_subestacao from '../../../../../assets/imgs/icone_subestacao.png';
import icone_casa from '../../../../../assets/imgs/icone_casa.png';
import icone_loja from '../../../../../assets/imgs/icone_loja.png';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ModuloPGMComponent from '../components/ModuloPGMComponent/index';
import ModuloSonoroComponent from '../components/ModuloSonoroComponent/index';
import CentralComponent from '../components/CentralComponent/index';
import ModuloGatewayComponent from '../components/ModuloGatewayComponent/index';
import ModuloSentinelComponent from '../components/ModuloSentinelComponent/index';
import PainelTriagemOcorrencias from '../components/PainelTriagemOcorrencias/index';
import PainelInstalacaoDetalhes from '../components/PainelInstalacaoDetalhes/index';
import GraficoDisponibilidade from '../../../../../components/GraficoDisponibilidade/GraficoDisponibilidade';

import './styles.css';

function processarIconeInstalacao(tipo) {
    if (tipo === 0) {
        return icone_casa;
    } else if (tipo === 1) {
        return icone_loja;
    } else if (tipo === 2) {
        return icone_subestacao;
    }
    return icone_casa;
}

const LocalComponent = ({ id_instalacao, tipo_icone, nome_mapa, instalacao, darkMode }) => {
    const status_central = instalacao.status_central;
    const isAlarm = instalacao.is_alarme || status_central === 2 || instalacao.status_geral === "ALARME";
    const isOnline = instalacao.is_online !== undefined ? instalacao.is_online : (instalacao.status_conexao === 1);
    const isArmada = instalacao.is_armada !== undefined ? instalacao.is_armada : (status_central === 1);

    let statusText, statusColor;
    if (isAlarm) {
        statusText = "ALARME CRÍTICO";
        statusColor = "#ef4444";
    } else if (isOnline) {
        if (isArmada) {
            statusText = "SISTEMA PROTEGIDO";
            statusColor = "#10b981";
        } else {
            statusText = "SISTEMA DESARMADO";
            statusColor = "#f59e0b";
        }
    } else {
        const diffMin = instalacao.tempo_inativo_minutos || 0;
        let timeString = "";
        if (diffMin < 60) {
            timeString = diffMin > 0 ? `HÁ ${diffMin} MIN` : "RECENTE";
        } else if (diffMin < 1440) {
            timeString = `HÁ ${Math.floor(diffMin / 60)} HORAS`;
        } else {
            timeString = `HÁ ${Math.floor(diffMin / 1440)} DIAS`;
        }
        statusText = `DESCONECTADO (${timeString})`;
        statusColor = "#64748b";
    }

    const handleClickGerenciar = (e) => {
        e.stopPropagation();
        const url = `/gestao/administracao/clientes/gerenciarcliente/instalacao/gerenciarinstalacao/${id_instalacao}/0`;
        window.open(url, '_blank');
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                background: darkMode ? '#1e293b' : '#ffffff',
                borderLeft: `4px solid ${statusColor}`,
                borderRadius: '8px 8px 0 0',
                mb: 0,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
            }}
        >
            <Box sx={{ p: 1.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                        width: 42, height: 42, borderRadius: '8px', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: `${statusColor}18`, border: `1px solid ${statusColor}30`
                    }}>
                        <img
                            alt="icon"
                            style={{ width: 24, height: 24, filter: darkMode ? 'brightness(1.15)' : 'none' }}
                            src={processarIconeInstalacao(tipo_icone)}
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ 
                            fontWeight: 800, fontSize: '0.95rem', 
                            color: darkMode ? '#f1f5f9' : '#1e293b',
                            letterSpacing: '-0.2px'
                        }}>
                            {nome_mapa?.toUpperCase()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.2 }}>
                            <div style={{ 
                                width: 7, height: 7, borderRadius: '50%', 
                                backgroundColor: statusColor,
                                boxShadow: `0 0 6px ${statusColor}`
                            }} />
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: statusColor, letterSpacing: '0.5px' }}>
                                {statusText}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Tooltip title="Gerenciar Instalação">
                    <IconButton 
                        onClick={handleClickGerenciar}
                        size="small"
                        sx={{ 
                            color: darkMode ? '#94a3b8' : '#64748b',
                            '&:hover': { color: '#0284c7', background: 'rgba(2, 132, 199, 0.1)' }
                        }}
                    >
                        <ManageAccountsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
};

export default function MonitoramentoOperacao() {
    const { idInst, pesquisa } = useParams();
    const history = useHistory();
    const location = useLocation();

    // Modo debug habilitado via parâmetro na URL: ?debug=true
    const isDebugMode = useMemo(() => {
        const search = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
        return new URLSearchParams(search).get('debug') === 'true';
    }, [location?.search]);

    const [darkMode] = useState(() => {
        const saved = Cookies.get('darkMode') || Cookies.get('tema');
        return saved === 'true' || saved === 'dark';
    });

    const themeV4 = useMemo(() => createCoreTheme({
        palette: {
            type: darkMode ? 'dark' : 'light',
            background: {
                default: darkMode ? '#0b1120' : '#f1f5f9',
                paper: darkMode ? '#1e293b' : '#ffffff',
            },
            text: {
                primary: darkMode ? '#f8fafc' : '#0f172a',
                secondary: darkMode ? '#94a3b8' : '#64748b',
            }
        }
    }), [darkMode]);

    const themeV5 = useMemo(() => createMuiTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            background: {
                default: darkMode ? '#0b1120' : '#f1f5f9',
                paper: darkMode ? '#1e293b' : '#ffffff',
            },
            text: {
                primary: darkMode ? '#f8fafc' : '#0f172a',
                secondary: darkMode ? '#94a3b8' : '#64748b',
            }
        }
    }), [darkMode]);

    // Dados e Contadores Pre-processados pelo Servidor
    const [snapshot, setSnapshot] = useState({
        total_instalacoes: 0,
        total_protegidas: 0,
        total_desarmadas: 0,
        total_desconectadas: 0,
        total_em_alarme: 0,
        instalacoes: []
    });

    const [loading, setLoading] = useState(!isDebugMode);
    const [isProcessandoCiclo, setIsProcessandoCiclo] = useState(false);
    const triggerFetchRef = useRef(null);
    const logsContainerRef = useRef(null);

    const [isConnected, setIsConnected] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Filtros e Pesquisa
    const [searchTerm, setSearchTerm] = useState(pesquisa ? decodeURIComponent(pesquisa) : "");
    const [statusFilter, setStatusFilter] = useState("TODAS"); // "TODAS", "PROTEGIDA", "DESARMADA", "DESCONECTADA", "ALARME"

    // Seleção de Instalação e Modais
    const [instalacaoSelecionada, setInstalacaoSelecionada] = useState(null);
    const [openGraficoDisponibilidade, setOpenGraficoDisponibilidade] = useState(false);

    // SIP
    const [activeCallRamal, setActiveCallRamal] = useState(null);
    const [sipStatus, setSipStatus] = useState("REGISTERED");
    const [useNativeCall, setUseNativeCall] = useState(true);

    const instalacaoSelecionadaRef = useRef(instalacaoSelecionada);
    instalacaoSelecionadaRef.current = instalacaoSelecionada;

    // Normalizador de dados para compatibilidade (suporta snapshot do backend ou array de produção)
    const normalizarSnapshot = useCallback((dadosRecebidos) => {
        if (!dadosRecebidos) return null;

        // Se já veio no formato OperacaoSnapshotDTO com total_instalacoes
        if (dadosRecebidos.instalacoes && typeof dadosRecebidos.total_instalacoes === 'number') {
            return dadosRecebidos;
        }

        // Se veio no formato array de instalações (legado / produção atual)
        if (Array.isArray(dadosRecebidos)) {
            const now = new Date();
            let prot = 0, desar = 0, desc = 0, alar = 0;

            const instProcessadas = dadosRecebidos.map(inst => {
                let instAlarme = false;
                let instOnline = false;
                let instArmada = false;
                let menorDiffMin = 999999;

                const searchTokens = [
                    inst.nome_mapa || '',
                    inst.identificador_integrador ? String(inst.identificador_integrador) : '',
                    inst.nome || '',
                    inst.sobrenome || '',
                    inst.logradouro || '',
                    inst.bairro || '',
                    inst.cidade || ''
                ];

                if (inst.equipamentos && Array.isArray(inst.equipamentos)) {
                    inst.equipamentos.forEach(eq => {
                        if (!eq) return;
                        const tipo = eq.tipo_equipamento_area_tecnica;
                        if (tipo === 0 || tipo === 30) {
                            const mod = tipo === 0 ? eq.modulo_pgm : eq.modulo_sonoro;
                            if (mod) {
                                if (mod.codigo) searchTokens.push(mod.codigo);

                                const last = new Date(mod.data_hora_ultima_resposta);
                                const diffMin = (now - last) / 1000 / 60;
                                if (diffMin < menorDiffMin) menorDiffMin = diffMin;

                                const eqAlarme = !!eq.alarme;
                                if (eqAlarme) instAlarme = true;

                                const eqOnline = eqAlarme || (diffMin < 3);
                                if (eqOnline) instOnline = true;

                                // Verifica se armado (com fallout para status_zonas para bater 100% com Dashboard)
                                let countsAsArmed = !!mod.armada;
                                if (tipo === 0 && !mod.status_central && mod.status_zonas) {
                                    countsAsArmed = (mod.status_zonas || "").split(";").some(l => {
                                        const p = l.split(",");
                                        if (p.length < 4) return false;
                                        const s = (p[3] || "").toLowerCase().trim();
                                        return s === "armada" || s === "true" || s === "1";
                                    });
                                }
                                if (mod.status_central) {
                                    const sc = mod.status_central.toLowerCase();
                                    if (sc.includes("away") || sc.includes("stay")) {
                                        countsAsArmed = true;
                                    }
                                }

                                if (eqOnline && countsAsArmed) {
                                    instArmada = true;
                                }
                            }
                        }
                    });
                }

                let statusGeral = "DESARMADA";
                if (instAlarme) {
                    statusGeral = "ALARME";
                    alar++;
                } else if (!instOnline) {
                    statusGeral = "DESCONECTADA";
                    desc++;
                } else if (instArmada) {
                    statusGeral = "PROTEGIDA";
                    prot++;
                } else {
                    statusGeral = "DESARMADA";
                    desar++;
                }

                searchTokens.push(statusGeral);
                if (instAlarme) searchTokens.push('alarme disparado');
                if (instOnline) searchTokens.push('online');
                else searchTokens.push('offline desconectado');
                if (instArmada) searchTokens.push('protegido armado');
                else searchTokens.push('desarmado inseguro');

                return {
                    ...inst,
                    status_geral: statusGeral,
                    is_alarme: instAlarme,
                    is_online: instOnline,
                    is_armada: instArmada,
                    tempo_inativo_minutos: menorDiffMin === 999999 ? 0 : Math.round(menorDiffMin),
                    search_index: searchTokens.join(' ').toLowerCase()
                };
            });

            return {
                total_instalacoes: instProcessadas.length,
                total_protegidas: prot,
                total_desarmadas: desar,
                total_desconectadas: desc,
                total_em_alarme: alar,
                instalacoes: instProcessadas
            };
        }

        return null;
    }, []);

    // Performance & Debug Monitor
    const startTimeRef = useRef(performance.now());
    const sendTimeRef = useRef(null);
    const [debugLogs, setDebugLogs] = useState([]);
    const [showDebugPanel, setShowDebugPanel] = useState(true);
    const [debugExpanded, setDebugExpanded] = useState(true);
    const [tempoAguardandoAoVivo, setTempoAguardandoAoVivo] = useState(null);
    const [debugSummary, setDebugSummary] = useState({
        wsStatus: 'Iniciando...',
        tempoConexao: null,
        tempoRespostaServidor: null,
        tamanhoPayload: null,
        totalInstalacoes: 0,
        tempoProcessamento: null,
        ultimoEnvio: null,
        ultimaResposta: null,
        idClienteResolvido: 'Pendente',
        origemDados: null
    });

    // Adiciona logs de cima para baixo (cronológico: primeiros no topo, novos adicionados abaixo)
    const addDebugLog = useCallback((etapa, detalhe = '') => {
        const logEntry = detalhe ? `${etapa} -> ${detalhe}` : etapa;
        console.log(logEntry);
        setDebugLogs(prev => [...prev, logEntry].slice(-100));
    }, []);

    // Auto-scroll para acompanhar novos logs no final do histórico
    useEffect(() => {
        if (logsContainerRef.current) {
            logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
        }
    }, [debugLogs]);

    // Conexão WebSocket em Tempo Real com Instrumentação e Ciclo Infinito / Debug
    useEffect(() => {
        let isMounted = true;
        let client = null;
        let waiting = false;
        let liveTimerInterval = null;
        let nextRequestTimer = null;

        const connectWS = async () => {
            const token = Cookies.get('token');
            const idUsuario = Cookies.get('id_usuario');

            if (!token) return;

            let idCliente = null;

            // 1. Busca perfil do cliente antes de conectar
            if (idUsuario) {
                const cachedCliente = sessionStorage.getItem(`id_cliente_${idUsuario}`);
                if (cachedCliente) {
                    idCliente = cachedCliente === 'ADMIN' ? null : cachedCliente;
                    if (isDebugMode) {
                        setDebugSummary(s => ({ ...s, idClienteResolvido: idCliente ? `Cliente #${idCliente}` : 'Administrador' }));
                    }
                } else {
                    try {
                        const res = await api.get(`/v1/protected/user/retornardadosusuario/${idUsuario}`, { 
                            headers: { Authorization: 'Bearer ' + token },
                            timeout: 3000
                        });
                        if (res.data && res.data.cliente && res.data.cliente.id_cliente) {
                            idCliente = res.data.cliente.id_cliente;
                            sessionStorage.setItem(`id_cliente_${idUsuario}`, String(idCliente));
                            if (isDebugMode) {
                                setDebugSummary(s => ({ ...s, idClienteResolvido: `Cliente #${idCliente}` }));
                            }
                        } else {
                            sessionStorage.setItem(`id_cliente_${idUsuario}`, 'ADMIN');
                            if (isDebugMode) {
                                setDebugSummary(s => ({ ...s, idClienteResolvido: 'Administrador (Todas)' }));
                            }
                        }
                    } catch (err) {
                        if (isDebugMode) {
                            setDebugSummary(s => ({ ...s, idClienteResolvido: 'Fallback Geral' }));
                        }
                    }
                }
            }

            if (!isMounted) return;

            // 2. Conecta ao WebSocket
            if (isDebugMode) {
                setDebugSummary(s => ({ ...s, wsStatus: 'Conectando ao WebSocket...' }));
            }

            try {
                const socket = new WebSocket(`wss://gruporosinetos.com/api/monitoramentowebsocket?token=${token}`);
                client = Stomp.over(socket);

                client.debug = () => {}; // Silencia ruído do STOMP para destacar os logs solicitados

                client.connect({}, () => {
                    if (!isMounted) {
                        client.disconnect();
                        return;
                    }
                    setIsConnected(true);
                    if (isDebugMode) {
                        setDebugSummary(s => ({ ...s, wsStatus: 'Conectado' }));
                    }

                    // Tópicos dedicados de operação (comprovadamente ativos no servidor)
                    const sendDest = idCliente ? `/app/operacao/requestData/cliente/${idCliente}` : "/app/operacao/requestData";
                    const topicDest = idCliente ? `/topic/operacao/dados/cliente/${idCliente}` : "/topic/operacao/dados";

                    const scheduleNext = (delayMs = 12000) => {
                        if (isDebugMode) return; // Em modo debug, não faz ciclo automático
                        if (nextRequestTimer) clearTimeout(nextRequestTimer);
                        nextRequestTimer = setTimeout(() => {
                            waiting = false;
                            sendRequest();
                        }, delayMs);
                    };

                    const sendRequest = () => {
                        if (client && client.connected && !waiting) {
                            waiting = true;
                            setIsProcessandoCiclo(true);
                            sendTimeRef.current = performance.now();
                            const agora = new Date().toLocaleString();

                            if (isDebugMode) {
                                addDebugLog(`pagina chamou o servidor para obter dados ${agora}`);
                                setDebugSummary(s => ({ 
                                    ...s, 
                                    ultimoEnvio: agora, 
                                    wsStatus: 'Aguardando resposta do servidor...' 
                                }));

                                // Inicia cronômetro ao vivo na tela
                                if (liveTimerInterval) clearInterval(liveTimerInterval);
                                liveTimerInterval = setInterval(() => {
                                    if (sendTimeRef.current) {
                                        const s = ((performance.now() - sendTimeRef.current) / 1000).toFixed(1);
                                        setTempoAguardandoAoVivo(`${s}s`);
                                    }
                                }, 500);
                            }

                            try {
                                client.send(sendDest, {}, JSON.stringify({}));
                            } catch (e) {
                                if (isDebugMode) {
                                    addDebugLog(`erro ao enviar requisição: ${e.message}`);
                                }
                                waiting = false;
                                setIsProcessandoCiclo(false);
                                if (liveTimerInterval) clearInterval(liveTimerInterval);
                                setTempoAguardandoAoVivo(null);
                                if (!isDebugMode) {
                                    scheduleNext(5000);
                                }
                            }
                        }
                    };

                    const handleIncomingData = (msg, origem) => {
                        if (liveTimerInterval) {
                            clearInterval(liveTimerInterval);
                            liveTimerInterval = null;
                        }
                        setTempoAguardandoAoVivo(null);

                        const agoraRecebido = new Date().toLocaleString();
                        const tMsg = performance.now();
                        const tempoAteReceber = sendTimeRef.current ? ((tMsg - sendTimeRef.current) / 1000).toFixed(2) : '0';
                        const tamanhoMB = (msg.body.length / (1024 * 1024)).toFixed(2);

                        if (isDebugMode) {
                            addDebugLog(`pagina recebeu os dados do servidor ${agoraRecebido}`);
                            addDebugLog(`tempo ate receber dados do servidor: ${tempoAteReceber} segundos`);
                            addDebugLog(`tamanho de dados: ${tamanhoMB} mega bytes`);
                            addDebugLog(`plotando dados na tela`);
                        }

                        try {
                            const tPlotInicio = performance.now();
                            const raw = JSON.parse(msg.body);
                            const parsed = normalizarSnapshot(raw);

                            if (parsed && isMounted) {
                                setSnapshot(parsed);
                                setLastSyncTime(new Date());
                                setLoading(false);

                                if (isDebugMode) {
                                    // Mede o tempo exato de processamento e pintura no DOM
                                    requestAnimationFrame(() => {
                                        const tempoPlotagemMs = (performance.now() - tPlotInicio).toFixed(1);
                                        const tempoPlotagemSegundos = ((performance.now() - tPlotInicio) / 1000).toFixed(3);
                                        const tempoTotalCiclo = sendTimeRef.current ? ((performance.now() - sendTimeRef.current) / 1000).toFixed(2) : tempoAteReceber;

                                        addDebugLog(`finalizacao de plotagem de dados em tempo: ${tempoPlotagemSegundos} segundos (${tempoPlotagemMs} ms)`);
                                        addDebugLog(`tempo total decorrido do ciclo: ${tempoTotalCiclo} segundos`);

                                        setDebugSummary(s => ({
                                            ...s,
                                            wsStatus: 'Concluído (Pronto)',
                                            tempoRespostaServidor: `${tempoAteReceber}s`,
                                            tamanhoPayload: `${tamanhoMB} MB`,
                                            totalInstalacoes: parsed.instalacoes?.length || 0,
                                            tempoProcessamento: `${tempoPlotagemMs}ms`,
                                            ultimaResposta: agoraRecebido,
                                            origemDados: origem
                                        }));
                                    });
                                }

                                // Mantém sincronizada a instalação atualmente aberta
                                if (instalacaoSelecionadaRef.current && parsed.instalacoes) {
                                    const matched = parsed.instalacoes.find(
                                        d => d.id_instalacao === instalacaoSelecionadaRef.current.id_instalacao
                                    );
                                    if (matched) {
                                        setInstalacaoSelecionada(matched);
                                    }
                                }

                                // Se veio idInst pela URL na primeira carga
                                if (idInst && parsed.instalacoes && !instalacaoSelecionadaRef.current) {
                                    const target = parsed.instalacoes.find(i => String(i.id_instalacao) === String(idInst));
                                    if (target) setInstalacaoSelecionada(target);
                                }
                            }
                        } catch (e) {
                            if (isDebugMode) {
                                addDebugLog(`erro no parse: ${e.message}`);
                            }
                        } finally {
                            waiting = false;
                            setIsProcessandoCiclo(false);
                            if (!isDebugMode) {
                                scheduleNext(12000);
                            }
                        }
                    };

                    // Inscreve no tópico dedicado de operação
                    client.subscribe(topicDest, (msg) => handleIncomingData(msg, topicDest));

                    // Registra a função para acionamento
                    triggerFetchRef.current = sendRequest;

                    if (!isDebugMode) {
                        // Modo de produção: ciclo indefinido e contínuo (começa e nunca termina)
                        sendRequest();
                    } else {
                        // Modo debug: aguarda clique no botão manual "Iniciar Ciclo"
                        setDebugSummary(s => ({ ...s, wsStatus: 'Conectado (Pronto para Iniciar Ciclo)' }));
                    }
                }, (error) => {
                    waiting = false;
                    setIsProcessandoCiclo(false);
                    triggerFetchRef.current = null;
                    if (liveTimerInterval) clearInterval(liveTimerInterval);
                    setTempoAguardandoAoVivo(null);
                    if (isDebugMode) {
                        addDebugLog('WebSocket Erro', String(error));
                        setDebugSummary(s => ({ ...s, wsStatus: `Erro / Desconectado (${String(error)})` }));
                    }
                    setIsConnected(false);
                    if (isMounted) {
                        setTimeout(connectWS, 5000);
                    }
                });
            } catch (err) {
                if (isDebugMode) {
                    addDebugLog('Erro Inicializar WS', err.message);
                }
            }
        };

        connectWS();

        return () => {
            isMounted = false;
            triggerFetchRef.current = null;
            if (nextRequestTimer) clearTimeout(nextRequestTimer);
            if (liveTimerInterval) clearInterval(liveTimerInterval);
            if (client && client.connected) {
                client.disconnect();
            }
        };
    }, [idInst, normalizarSnapshot, addDebugLog, isDebugMode]);

    // 3. Filtragem Ultra-Leve O(1) pelo search_index pré-computado
    const instalacoesFiltradas = useMemo(() => {
        if (!snapshot.instalacoes) return [];

        const term = searchTerm.trim().toLowerCase();
        const hasTerm = term.length > 0;
        const hasStatus = statusFilter !== "TODAS";

        if (!hasTerm && !hasStatus) {
            return snapshot.instalacoes;
        }

        return snapshot.instalacoes.filter(inst => {
            // Filtro por status
            if (hasStatus) {
                if (inst.status_geral !== statusFilter) return false;
            }

            // Filtro por palavra-chave usando o índice pré-calculado
            if (hasTerm) {
                const index = inst.search_index || `${inst.nome_mapa || ''} ${inst.identificador_integrador || ''}`.toLowerCase();
                if (!index.includes(term)) return false;
            }

            return true;
        });
    }, [snapshot.instalacoes, searchTerm, statusFilter]);

    // Disparar Softphone Global
    const handleOpenGlobalSip = () => {
        if (window.openGlobalWebphone) {
            window.openGlobalWebphone();
        } else {
            window.postMessage({ type: 'OPEN_SOFTPHONE' }, '*');
        }
    };

    const handleSipCall = (ramal) => {
        setActiveCallRamal(ramal);
        window.postMessage({ type: 'CALL_NUMBER', number: ramal }, '*');
    };

    const handleSipHangup = () => {
        setActiveCallRamal(null);
        window.postMessage({ type: 'HANGUP_CALL' }, '*');
    };

    const toggleSipMode = () => {
        setUseNativeCall(prev => !prev);
    };

    const toggleStatusFilter = (status) => {
        setStatusFilter(prev => prev === status ? "TODAS" : status);
    };

    function returnDescricaoEndereco(objeto) {
        if (!objeto) return "";
        return `${objeto.logradouro || ''}, ${objeto.numero || ''}, ${objeto.bairro || ''} - ${objeto.cidade || ''}/${objeto.estado || ''}`;
    }

    return (
        <MuiThemeProvider theme={themeV5}>
            <CoreThemeProvider theme={themeV4}>
                <div className="operacao-container" style={{ backgroundColor: themeV4.palette.background.default, color: themeV4.palette.text.primary }}>
                    <NavegadorGestao
                        AreaTecnica={true}
                        Monitoramento={true}
                        MonitoramentoOperacao={true}
                        corFundo={darkMode ? '#1e293b' : '#ffffff'}
                        travado={true}
                        corTexto={darkMode ? '#ffffff' : '#0f172a'}
                    />

                    {/* PAINEL DE DIAGNÓSTICO & MONITOR DE PERFORMANCE EM TEMPO REAL (Apenas com ?debug=true) */}
                    {isDebugMode && showDebugPanel && (
                        <Box sx={{ mx: 2.5, mt: 1.5, mb: 0.5 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    background: darkMode ? 'rgba(15, 23, 42, 0.85)' : '#e2e8f0',
                                    border: `1px solid ${darkMode ? 'rgba(2, 132, 199, 0.3)' : 'rgba(2, 132, 199, 0.4)'}`,
                                    color: darkMode ? '#f8fafc' : '#0f172a'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#0284c7', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            🛠️ DIAGNÓSTICO OPERAÇÃO:
                                        </Typography>

                                        {/* Botão de Disparo Manual de Ciclo Único */}
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={isProcessandoCiclo || !isConnected}
                                            onClick={() => {
                                                if (triggerFetchRef.current) {
                                                    triggerFetchRef.current();
                                                }
                                            }}
                                            startIcon={isProcessandoCiclo ? <CircularProgress size={13} color="inherit" /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: '0.74rem',
                                                py: 0.3,
                                                px: 1.2,
                                                backgroundColor: isProcessandoCiclo ? '#475569' : '#0284c7',
                                                '&:hover': { backgroundColor: '#0369a1' },
                                                textTransform: 'none',
                                                boxShadow: isProcessandoCiclo ? 'none' : '0 2px 4px rgba(2,132,199,0.3)'
                                            }}
                                        >
                                            {isProcessandoCiclo ? 'Processando Ciclo...' : 'Iniciar Ciclo'}
                                        </Button>

                                        <Chip 
                                            size="small" 
                                            label={`WS: ${debugSummary.wsStatus}`} 
                                            sx={{ 
                                                fontWeight: 700, fontSize: '0.72rem',
                                                backgroundColor: isConnected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                                                color: isConnected ? '#10b981' : '#f59e0b',
                                                border: '1px solid currentColor'
                                            }} 
                                        />
                                        {tempoAguardandoAoVivo && (
                                            <Chip 
                                                size="small" 
                                                label={`⏳ Processando no Servidor: ${tempoAguardandoAoVivo}`} 
                                                sx={{ 
                                                    fontWeight: 800, fontSize: '0.74rem',
                                                    backgroundColor: parseFloat(tempoAguardandoAoVivo) > 15 ? 'rgba(239, 68, 68, 0.25)' : 'rgba(245, 158, 11, 0.25)',
                                                    color: parseFloat(tempoAguardandoAoVivo) > 15 ? '#ef4444' : '#f59e0b',
                                                    border: '1px solid currentColor'
                                                }} 
                                            />
                                        )}
                                        <Chip 
                                            size="small" 
                                            label={`Perfil: ${debugSummary.idClienteResolvido}`} 
                                            sx={{ fontWeight: 600, fontSize: '0.72rem' }} 
                                        />
                                        {debugSummary.tempoRespostaServidor && (
                                            <Chip 
                                                size="small" 
                                                label={`⏱️ Resposta Servidor: ${debugSummary.tempoRespostaServidor}`} 
                                                sx={{ 
                                                    fontWeight: 700, fontSize: '0.72rem',
                                                    backgroundColor: parseFloat(debugSummary.tempoRespostaServidor) > 10 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                                                    color: parseFloat(debugSummary.tempoRespostaServidor) > 10 ? '#ef4444' : '#10b981'
                                                }} 
                                            />
                                        )}
                                        {debugSummary.tamanhoPayload && (
                                            <Chip 
                                                size="small" 
                                                label={`📦 Payload: ${debugSummary.tamanhoPayload}`} 
                                                sx={{ fontWeight: 600, fontSize: '0.72rem' }} 
                                            />
                                        )}
                                        {debugSummary.totalInstalacoes > 0 && (
                                            <Chip 
                                                size="small" 
                                                label={`🏢 Plotadas: ${debugSummary.totalInstalacoes} em ${debugSummary.tempoProcessamento || '0ms'}`} 
                                                sx={{ fontWeight: 700, fontSize: '0.72rem', backgroundColor: 'rgba(2, 132, 199, 0.2)', color: '#0284c7' }} 
                                            />
                                        )}
                                        {debugSummary.origemDados && (
                                            <Chip 
                                                size="small" 
                                                label={`Tópico: ${debugSummary.origemDados}`} 
                                                sx={{ fontWeight: 600, fontSize: '0.70rem', color: darkMode ? '#94a3b8' : '#64748b' }} 
                                            />
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setDebugExpanded(prev => !prev)}
                                            sx={{ fontSize: '0.68rem', fontWeight: 700, py: 0.2, px: 1, borderColor: '#0284c7', color: '#0284c7' }}
                                        >
                                            {debugExpanded ? 'Ocultar Detalhes' : 'Ver Linha do Tempo'}
                                        </Button>
                                        <IconButton size="small" onClick={() => setShowDebugPanel(false)} sx={{ color: darkMode ? '#94a3b8' : '#64748b' }}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {debugExpanded && (
                                    <Box 
                                        ref={logsContainerRef}
                                        sx={{ 
                                            mt: 1.5, pt: 1, borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                                            maxHeight: '190px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.72rem',
                                            background: darkMode ? '#0b1120' : '#f8fafc', p: 1.2, borderRadius: '4px'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, pb: 0.5, borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
                                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#0284c7', letterSpacing: '0.5px' }}>
                                                LOGS EM TEMPO REAL (DE CIMA PARA BAIXO)
                                            </Typography>
                                            <Button 
                                                size="small" 
                                                variant="text" 
                                                onClick={() => setDebugLogs([])}
                                                sx={{ fontSize: '0.65rem', py: 0, px: 0.8, minWidth: 'auto', color: darkMode ? '#94a3b8' : '#64748b' }}
                                            >
                                                Limpar Logs
                                            </Button>
                                        </Box>
                                        {debugLogs.length === 0 ? (
                                            <div style={{ color: darkMode ? '#64748b' : '#94a3b8', fontStyle: 'italic' }}>
                                                Aguardando comando. Clique em "Iniciar Ciclo" para disparar a busca.
                                            </div>
                                        ) : (
                                            debugLogs.map((log, idx) => (
                                                <div key={idx} style={{ padding: '3px 0', borderBottom: '1px dotted rgba(100,116,139,0.15)', whiteSpace: 'pre-wrap' }}>
                                                    {log}
                                                </div>
                                            ))
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )}

                    {/* CABEÇALHO COM CONTADORES CLÁSSICOS DE MONITORAMENTO E STATUS DE RECEPÇÃO */}
                    <div className="operacao-header-bar">
                        {/* Linha Superior Discreta: Status em Tempo Real da Recepção de Dados */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            mb: 1.5,
                            flexWrap: 'wrap',
                            gap: 1.5 
                        }}>
                            <Typography sx={{ 
                                fontSize: '0.88rem', 
                                fontWeight: 800, 
                                letterSpacing: '0.6px',
                                color: darkMode ? '#cbd5e1' : '#334155',
                                textTransform: 'uppercase',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.8
                            }}>
                                Central de Operação & Monitoramento
                            </Typography>

                            {/* Indicador Discreto e Visível de Recepção e Busca */}
                            <Tooltip title={isProcessandoCiclo ? "Buscando dados no servidor..." : (lastSyncTime ? "Dados recebidos e sincronizados com sucesso." : "Aguardando conexão...")}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1.8,
                                        py: 0.5,
                                        borderRadius: '20px',
                                        background: darkMode ? 'rgba(30, 41, 59, 0.95)' : '#ffffff',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.14)'}`,
                                        boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 6px rgba(0,0,0,0.06)',
                                        cursor: 'default',
                                        userSelect: 'none',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <SyncIcon 
                                        sx={{ 
                                            fontSize: 18,
                                            color: isProcessandoCiclo ? (darkMode ? '#38bdf8' : '#0284c7') : (lastSyncTime ? '#10b981' : (darkMode ? '#94a3b8' : '#64748b')),
                                            animation: isProcessandoCiclo ? 'operacao-spin 1.2s linear infinite' : 'none',
                                            '@keyframes operacao-spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }} 
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: darkMode ? '#94a3b8' : '#64748b'
                                        }}
                                    >
                                        Última recepção:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.78rem',
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color: darkMode ? '#f8fafc' : '#0f172a'
                                        }}
                                    >
                                        {lastSyncTime ? (
                                            `${lastSyncTime.toLocaleDateString('pt-BR')} ${lastSyncTime.toLocaleTimeString('pt-BR')}`
                                        ) : (
                                            isProcessandoCiclo ? 'Buscando...' : 'Aguardando sincronização...'
                                        )}
                                    </Typography>
                                    {isProcessandoCiclo && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 800,
                                                color: darkMode ? '#38bdf8' : '#0284c7',
                                                ml: 0.5
                                            }}
                                        >
                                            (Buscando...)
                                        </Typography>
                                    )}
                                </Box>
                            </Tooltip>
                        </Box>

                        <Grid container spacing={2} alignItems="center">
                            {/* Card 1: Protegidas */}
                            <Grid item xs={6} sm={6} md={3} lg={3}>
                                <Paper
                                    elevation={0}
                                    onClick={() => toggleStatusFilter("PROTEGIDA")}
                                    className={`operacao-summary-card status-protegida ${statusFilter === "PROTEGIDA" ? 'active-filter' : ''}`}
                                    sx={{
                                        background: darkMode ? '#1e293b' : '#ffffff',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Protegidas
                                            </Typography>
                                            <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: '#10b981', lineHeight: 1.1, fontFamily: 'monospace' }}>
                                                {snapshot.total_protegidas}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.12)', p: 1.2, borderRadius: '8px' }}>
                                            <SecurityIcon fontSize="medium" />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Card 2: Desarmadas */}
                            <Grid item xs={6} sm={6} md={3} lg={3}>
                                <Paper
                                    elevation={0}
                                    onClick={() => toggleStatusFilter("DESARMADA")}
                                    className={`operacao-summary-card status-desarmada ${statusFilter === "DESARMADA" ? 'active-filter' : ''}`}
                                    sx={{
                                        background: darkMode ? '#1e293b' : '#ffffff',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Desarmadas
                                            </Typography>
                                            <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: '#f59e0b', lineHeight: 1.1, fontFamily: 'monospace' }}>
                                                {snapshot.total_desarmadas}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.12)', p: 1.2, borderRadius: '8px' }}>
                                            <SensorsIcon fontSize="medium" />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Card 3: Desconectadas */}
                            <Grid item xs={6} sm={6} md={3} lg={3}>
                                <Paper
                                    elevation={0}
                                    onClick={() => toggleStatusFilter("DESCONECTADA")}
                                    className={`operacao-summary-card status-desconectada ${statusFilter === "DESCONECTADA" ? 'active-filter' : ''}`}
                                    sx={{
                                        background: darkMode ? '#1e293b' : '#ffffff',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Desconectadas
                                            </Typography>
                                            <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: '#64748b', lineHeight: 1.1, fontFamily: 'monospace' }}>
                                                {snapshot.total_desconectadas}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: '#64748b', background: 'rgba(100, 116, 139, 0.12)', p: 1.2, borderRadius: '8px' }}>
                                            <SignalCellularConnectedNoInternet4BarIcon fontSize="medium" />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Card 4: Em Alarme */}
                            <Grid item xs={6} sm={6} md={3} lg={3}>
                                <Paper
                                    elevation={0}
                                    onClick={() => toggleStatusFilter("ALARME")}
                                    className={`operacao-summary-card status-alarme ${snapshot.total_em_alarme > 0 ? 'has-alarm' : ''} ${statusFilter === "ALARME" ? 'active-filter' : ''}`}
                                    sx={{
                                        background: darkMode ? '#1e293b' : '#ffffff',
                                        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Em Alarme
                                            </Typography>
                                            <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: '#ef4444', lineHeight: 1.1, fontFamily: 'monospace' }}>
                                                {snapshot.total_em_alarme}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.12)', p: 1.2, borderRadius: '8px' }}>
                                            <NotificationsActiveIcon fontSize="medium" />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </div>

                    {/* BARRA DE CONTROLE: PESQUISA, FILTROS, TOTAL DISCRETO E BOTÃO SIP GLOBAL */}
                    <div className="operacao-search-box">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Pesquisar instalação por nome, identificador, código de módulo ou endereço..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: darkMode ? '#94a3b8' : '#64748b', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm("")}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    background: darkMode ? '#1e293b' : '#ffffff',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500
                                }
                            }}
                        />

                        {/* Indicador Discreto do Total */}
                        <Box
                            className="operacao-total-badge"
                            sx={{
                                background: darkMode ? 'rgba(30, 41, 59, 0.8)' : '#e2e8f0',
                                color: darkMode ? '#cbd5e1' : '#334155',
                                border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FiberManualRecordIcon sx={{ fontSize: 10, color: isConnected ? '#10b981' : '#f59e0b' }} />
                            <span>Total: <strong>{snapshot.total_instalacoes}</strong></span>
                        </Box>

                        {/* Filtro Ativo Chip (se houver) */}
                        {statusFilter !== "TODAS" && (
                            <Chip
                                label={`Filtro: ${statusFilter}`}
                                size="small"
                                onDelete={() => setStatusFilter("TODAS")}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.72rem',
                                    backgroundColor: statusFilter === 'PROTEGIDA' ? 'rgba(16, 185, 129, 0.15)' :
                                                     statusFilter === 'DESARMADA' ? 'rgba(245, 158, 11, 0.15)' :
                                                     statusFilter === 'DESCONECTADA' ? 'rgba(100, 116, 139, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                    color: statusFilter === 'PROTEGIDA' ? '#10b981' :
                                           statusFilter === 'DESARMADA' ? '#f59e0b' :
                                           statusFilter === 'DESCONECTADA' ? '#64748b' : '#ef4444',
                                    border: `1px solid currentColor`
                                }}
                            />
                        )}

                        {/* Botão de Disparo Manual de Ciclo Único (Apenas com ?debug=true) */}
                        {isDebugMode && (
                            <Button
                                variant="contained"
                                disabled={isProcessandoCiclo || !isConnected}
                                onClick={() => {
                                    if (triggerFetchRef.current) {
                                        triggerFetchRef.current();
                                    }
                                }}
                                startIcon={isProcessandoCiclo ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    height: 38,
                                    backgroundColor: isProcessandoCiclo ? '#475569' : '#0284c7',
                                    '&:hover': { backgroundColor: '#0369a1' },
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    px: 2
                                }}
                            >
                                {isProcessandoCiclo ? 'Processando...' : 'Iniciar Ciclo'}
                            </Button>
                        )}

                        {/* Botão Global de SIP Softphone */}
                        <Button
                            variant="contained"
                            className="operacao-sip-btn"
                            startIcon={<PhoneInTalkIcon />}
                            onClick={handleOpenGlobalSip}
                        >
                            SIP
                        </Button>
                    </div>

                    {/* CORPO PRINCIPAL: LISTA DE CARDS À ESQUERDA + PAINEL DE DETALHES/TRIAGEM À DIREITA */}
                    <Box sx={{ px: 2.5, pb: 2 }}>
                        {loading ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <CircularProgress size={36} sx={{ color: '#0284c7' }} />
                                <Typography sx={{ mt: 2, fontSize: '0.85rem', color: darkMode ? '#94a3b8' : '#64748b' }}>
                                    Carregando status do monitoramento...
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {/* Coluna 1: Lista de Cards de Instalações */}
                                <Grid item xs={12} sm={6} md={5} lg={4}>
                                    <Box className="operacao-list-scroll">
                                        {instalacoesFiltradas.length === 0 ? (
                                            <Paper
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    background: darkMode ? '#1e293b' : '#ffffff',
                                                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '0.85rem', color: darkMode ? '#94a3b8' : '#64748b' }}>
                                                    {snapshot.total_instalacoes === 0 
                                                        ? (isDebugMode 
                                                            ? 'Nenhuma busca realizada. Clique em "Iniciar Ciclo" acima para carregar os dados.' 
                                                            : 'Aguardando dados das instalações...')
                                                        : 'Nenhuma instalação encontrada com os filtros atuais.'}
                                                </Typography>
                                            </Paper>
                                        ) : (
                                            instalacoesFiltradas.map((instalacao) => {
                                                const isSelected = instalacaoSelecionada?.id_instalacao === instalacao.id_instalacao;
                                                const status = instalacao.status_geral || "DESARMADA";
                                                const statusColor = status === "PROTEGIDA" ? "#10b981" :
                                                                    status === "DESARMADA" ? "#f59e0b" :
                                                                    status === "DESCONECTADA" ? "#64748b" : "#ef4444";

                                                return (
                                                    <Paper
                                                        key={instalacao.id_instalacao}
                                                        elevation={0}
                                                        onClick={() => setInstalacaoSelecionada(instalacao)}
                                                        className={`operacao-card-instalacao ${isSelected ? 'selected' : ''}`}
                                                        sx={{
                                                            background: isSelected 
                                                                ? (darkMode ? '#1e293b' : '#ffffff') 
                                                                : (darkMode ? '#131e30' : '#ffffff'),
                                                            border: `1px solid ${isSelected ? '#0284c7' : (darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)')}`,
                                                        }}
                                                    >
                                                        {/* Faixa de Status Clássica da Instalação */}
                                                        <div 
                                                            className="operacao-card-status-strip"
                                                            style={{ 
                                                                backgroundColor: `${statusColor}15`, 
                                                                color: statusColor,
                                                                borderBottom: `1px solid ${statusColor}30`
                                                            }}
                                                        >
                                                            <span>{status}</span>
                                                            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: darkMode ? '#94a3b8' : '#64748b' }}>
                                                                ID #{instalacao.id_instalacao}
                                                            </span>
                                                        </div>

                                                        {/* Componente de Cabeçalho do Local */}
                                                        <LocalComponent
                                                            id_instalacao={instalacao.id_instalacao}
                                                            tipo_icone={instalacao.icone_mapa}
                                                            nome_mapa={instalacao.nome_mapa}
                                                            instalacao={instalacao}
                                                            darkMode={darkMode}
                                                        />

                                                        {/* Módulos e Equipamentos Reutilizados */}
                                                        {instalacao.equipamentos && Array.isArray(instalacao.equipamentos) && (
                                                            <Grid
                                                                container
                                                                spacing={1}
                                                                sx={{
                                                                    background: darkMode ? '#0f172a' : '#f8fafc',
                                                                    p: 1.5,
                                                                    m: 0,
                                                                    width: '100% !important',
                                                                    borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                                                                }}
                                                            >
                                                                {instalacao.equipamentos.map((equipamento) => (
                                                                    <React.Fragment key={equipamento.id_equipamento}>
                                                                        {equipamento?.tipo_equipamento_area_tecnica === 0 && equipamento?.modulo_pgm?.id_modulo_pgm > 0 && (
                                                                            <ModuloPGMComponent tipo={0} equipamento={equipamento} flag={0} />
                                                                        )}
                                                                        {equipamento?.tipo_equipamento_area_tecnica === 70 && (
                                                                            <CentralComponent tipo={70} equipamento={equipamento} flag={0} />
                                                                        )}
                                                                        {equipamento?.tipo_equipamento_area_tecnica === 30 && equipamento?.modulo_sonoro?.id_modulo_sonoro > 0 && (
                                                                            <ModuloSonoroComponent
                                                                                tipo={30}
                                                                                equipamento={equipamento}
                                                                                flag={0}
                                                                                id_instalacao={instalacao?.id_instalacao}
                                                                                useNativeCall={useNativeCall}
                                                                                setUseNativeCall={toggleSipMode}
                                                                                sipStatus={sipStatus}
                                                                                onSipCall={handleSipCall}
                                                                                onSipHangup={handleSipHangup}
                                                                                handleOpenGraficoDisponibilidade={() => {
                                                                                    setInstalacaoSelecionada(instalacao);
                                                                                    setOpenGraficoDisponibilidade(true);
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {equipamento?.tipo_equipamento_area_tecnica === 5 && (
                                                                            <ModuloGatewayComponent tipo={5} equipamento={equipamento} flag={0} />
                                                                        )}
                                                                        {equipamento?.tipo_equipamento_area_tecnica === 8 && (
                                                                            <ModuloSentinelComponent tipo={8} equipamento={equipamento} flag={0} />
                                                                        )}
                                                                    </React.Fragment>
                                                                ))}
                                                            </Grid>
                                                        )}
                                                    </Paper>
                                                );
                                            })
                                        )}
                                    </Box>
                                </Grid>

                                {/* Coluna 2: Triagem de Ocorrências ou Painel de Detalhes da Instalação */}
                                <Grid item xs={12} sm={6} md={7} lg={8}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            height: 'calc(100vh - 40px)',
                                            position: 'sticky',
                                            top: '20px',
                                            overflow: 'hidden',
                                            borderRadius: '8px',
                                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                                            background: darkMode ? '#1e293b' : '#ffffff'
                                        }}
                                    >
                                        <PainelTriagemOcorrencias 
                                            onSelectInstalacao={(inst) => setInstalacaoSelecionada(inst)} 
                                            darkMode={darkMode}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                    </Box>

                    {/* Detalhes da Instalação Selecionada (Drawer / Mosaico / Câmeras / Acionamentos) */}
                    {instalacaoSelecionada && (
                        <PainelInstalacaoDetalhes
                            instalacaoSelecionada={instalacaoSelecionada}
                            setInstalacaoSelecionada={setInstalacaoSelecionada}
                            larguraOriginal={window.innerWidth}
                            limiar={768}
                            darkMode={darkMode}
                            activeCallRamal={activeCallRamal}
                            handleSipHangup={handleSipHangup}
                            handleSipCall={handleSipCall}
                            returnDescricaoEndereco={returnDescricaoEndereco}
                            handleOpenGraficoDisponibilidade={() => setOpenGraficoDisponibilidade(true)}
                            useNativeCall={useNativeCall}
                            toggleSipMode={toggleSipMode}
                            sipStatus={sipStatus}
                        />
                    )}

                    {/* Modal do Gráfico de Disponibilidade */}
                    {openGraficoDisponibilidade && instalacaoSelecionada && (
                        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999 }}>
                            <GraficoDisponibilidade
                                idInstalacao={instalacaoSelecionada.id_instalacao}
                                instalacao={instalacaoSelecionada}
                                onClose={() => setOpenGraficoDisponibilidade(false)}
                            />
                        </div>
                    )}
                </div>
            </CoreThemeProvider>
        </MuiThemeProvider>
    );
}
